/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Workspace setup (IT & platform admins)", description = "Set up secure access, governance, and user roles in your Hightouch workspace so data and marketing teams can activate safely.", lastUpdatedDate = new Date(1790553600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Workspace setup (IT & platform admins)",
        description: "Set up secure access, governance, and user roles in your Hightouch workspace so data and marketing teams can activate safely.",
        lastUpdatedDate: "Mon Sep 28 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Setup checklist",
          "depth": 0,
          "id": "setup-checklist"
        }, {
          "value": "1. Learn the building blocks",
          "depth": 1,
          "id": "1-learn-the-building-blocks"
        }, {
          "value": "2. Create your workspace",
          "depth": 1,
          "id": "2-create-your-workspace"
        }, {
          "value": "3. Set up access control",
          "depth": 1,
          "id": "3-set-up-access-control"
        }, {
          "value": "4. Plan your governance",
          "depth": 1,
          "id": "4-plan-your-governance"
        }, {
          "value": "5. Secure your infrastructure",
          "depth": 1,
          "id": "5-secure-your-infrastructure"
        }, {
          "value": "6. Organize resources (optional)",
          "depth": 1,
          "id": "6-organize-resources-optional"
        }, {
          "value": "Readiness checklist",
          "depth": 0,
          "id": "readiness-checklist"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      h3: "h3",
      a: "a",
      img: "img",
      br: "br",
      ul: "ul",
      li: "li",
      input: "input"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsx("li", {
                  children: "Admin or organization-owner access to your Hightouch account"
                }), _jsx("li", {
                  children: "Your organization's SSO provider details (if using SSO)"
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Configure your Hightouch workspace to ensure secure access, clear permissions, and consistent governance across teams."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "setup-checklist",
        children: "Setup checklist"
      }), "\n", _jsx(_components.h3, {
        id: "1-learn-the-building-blocks",
        children: "1. Learn the building blocks"
      }), "\n", _jsx(_components.p, {
        children: "Before configuring anything, familiarize yourself with how Hightouch is organized: organizations, workspaces, user groups, roles, environments, and Spaces."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/workspace-management/overview",
          children: _jsx(_components.strong, {
            children: "Core concepts"
          })
        }), " — organizations, workspaces, user groups, roles, environments, and Spaces"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "get-started/workspace-diagram.png",
          alt: "Workspace design diagram"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Most teams use a single production workspace with ", _jsx(_components.a, {
          href: "/workspace-management/spaces",
          children: "Spaces"
        }), ", permissions, and ", _jsx(_components.a, {
          href: "/customer-studio/subsets",
          children: "subsets"
        }), " to separate teams. Use additional workspaces when you need strict data isolation or separate dev and prod environments. See ", _jsx(_components.a, {
          href: "/workspace-management/workspaces",
          children: "Workspaces"
        }), " for guidance on when to add a workspace."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "2-create-your-workspace",
        children: "2. Create your workspace"
      }), "\n", _jsx(_components.p, {
        children: "Set up your organization and first workspace. Choose a region, pick a workspace name and slug, and walk through the onboarding checklist."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/workspace-management/workspaces",
          children: _jsx(_components.strong, {
            children: "Workspaces"
          })
        }), " — create, configure, switch, and delete workspaces"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "3-set-up-access-control",
        children: "3. Set up access control"
      }), "\n", _jsx(_components.p, {
        children: "Configure how your team authenticates and what they can access. Set up SSO and SCIM to automate provisioning, map identity provider groups to Hightouch user groups, and assign roles."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/workspace-management/sso",
          children: _jsx(_components.strong, {
            children: "SSO setup"
          })
        }), " — connect your identity provider for centralized authentication", _jsx(_components.br, {}), "\n", _jsx(_components.a, {
          href: "/workspace-management/user-management",
          children: _jsx(_components.strong, {
            children: "Manage users and groups"
          })
        }), " — invite users, assign groups, manage membership", _jsx(_components.br, {}), "\n", _jsx(_components.a, {
          href: "/workspace-management/roles",
          children: _jsx(_components.strong, {
            children: "Roles"
          })
        }), " — pre-built and custom permission sets", _jsx(_components.br, {}), "\n", _jsx(_components.a, {
          href: "/workspace-management/user-management#transfer-organization-admin-access",
          children: _jsx(_components.strong, {
            children: "Transfer org admin access"
          })
        }), " — hand off admin privileges when team members change"]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.img, {
          src: "get-started/user-group-diagram.png",
          alt: "User groups diagram"
        }), "\n", _jsx(_components.em, {
          children: "User groups manage access across workspaces, with roles assigned at the group level."
        })]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Start with pre-built roles during onboarding. Move to custom roles once your\norg structure or compliance needs require it."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "4-plan-your-governance",
        children: "4. Plan your governance"
      }), "\n", _jsx(_components.p, {
        children: "Decide how teams should collaborate within your workspace: who can publish changes, what data they can reach, and what requires approval. Hightouch offers several controls that layer on top of each other."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/workspace-management/governance",
          children: _jsx(_components.strong, {
            children: "Governance best practices"
          })
        }), " — when to use each control and how they layer together", _jsx(_components.br, {}), "\n", _jsx(_components.a, {
          href: "/workspace-management/environments",
          children: _jsx(_components.strong, {
            children: "Environments"
          })
        }), " — dev/staging/prod promotion within a workspace", _jsx(_components.br, {}), "\n", _jsx(_components.a, {
          href: "/workspace-management/approval-flows",
          children: _jsx(_components.strong, {
            children: "Approval flows"
          })
        }), " — require review before changes go live", _jsx(_components.br, {}), "\n", _jsx(_components.a, {
          href: "/workspace-management/spaces",
          children: _jsx(_components.strong, {
            children: "Spaces"
          })
        }), " — scope resource visibility across teams", _jsx(_components.br, {}), "\n", _jsx(_components.a, {
          href: "/customer-studio/subsets",
          children: _jsx(_components.strong, {
            children: "Subsets"
          })
        }), " — limit which records marketers can query by region, brand, or compliance boundary", _jsx(_components.br, {}), "\n", _jsx(_components.a, {
          href: "/customer-studio/destination-rules",
          children: _jsx(_components.strong, {
            children: "Destination rules"
          })
        }), " — control which records can be sent to specific destinations", _jsx(_components.br, {}), "\n", _jsx(_components.a, {
          href: "/customer-studio/templates",
          children: _jsx(_components.strong, {
            children: "Templates"
          })
        }), " — standardize audience and sync setup so marketers start from approved patterns", _jsx(_components.br, {}), "\n", _jsx(_components.a, {
          href: "/workspace-management/audit-logs",
          children: _jsx(_components.strong, {
            children: "Audit logs"
          })
        }), " — track all in-app user actions for 90 days"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "5-secure-your-infrastructure",
        children: "5. Secure your infrastructure"
      }), "\n", _jsx(_components.p, {
        children: "Configure storage, networking, and monitoring to meet your security and compliance requirements."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/security/overview",
          children: _jsx(_components.strong, {
            children: "Security overview"
          })
        }), " — encryption, compliance, and hosting details", _jsx(_components.br, {}), "\n", _jsx(_components.a, {
          href: "/security/storage",
          children: _jsx(_components.strong, {
            children: "Storage"
          })
        }), " — configure self-hosted storage for data-at-rest", _jsx(_components.br, {}), "\n", _jsx(_components.a, {
          href: "/security/networking",
          children: _jsx(_components.strong, {
            children: "Networking"
          })
        }), " — tunnels, PrivateLink, IP allowlisting", _jsx(_components.br, {}), "\n", _jsx(_components.a, {
          href: "/syncs/alerting",
          children: _jsx(_components.strong, {
            children: "Alerting"
          })
        }), " — sync failure notifications via Slack, email, PagerDuty, or Datadog", _jsx(_components.br, {}), "\n", _jsx(_components.a, {
          href: "/syncs/warehouse-sync-logs",
          children: _jsx(_components.strong, {
            children: "Sync logs"
          })
        }), " — write sync history to your warehouse"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "6-organize-resources-optional",
        children: "6. Organize resources (optional)"
      }), "\n", _jsx(_components.p, {
        children: "As your workspace grows, use folders, labels, and filters to keep things navigable."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.a, {
          href: "/workspace-management/folders-filters",
          children: _jsx(_components.strong, {
            children: "Folders, filters, and labels"
          })
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "readiness-checklist",
        children: "Readiness checklist"
      }), "\n", _jsx(_components.p, {
        children: "Before marketers can start building audiences, the workspace needs to be fully configured. Use this checklist to verify that all setup is complete across teams."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Platform admin responsibilities:"
        })
      }), "\n", _jsxs(_components.ul, {
        className: "contains-task-list",
        children: ["\n", _jsxs(_components.li, {
          className: "task-list-item",
          children: [_jsx(_components.input, {
            type: "checkbox",
            disabled: true
          }), " ", "Workspace created and region selected"]
        }), "\n", _jsxs(_components.li, {
          className: "task-list-item",
          children: [_jsx(_components.input, {
            type: "checkbox",
            disabled: true
          }), " ", "SSO and SCIM configured (if applicable)"]
        }), "\n", _jsxs(_components.li, {
          className: "task-list-item",
          children: [_jsx(_components.input, {
            type: "checkbox",
            disabled: true
          }), " ", "User groups created and roles assigned"]
        }), "\n", _jsxs(_components.li, {
          className: "task-list-item",
          children: [_jsx(_components.input, {
            type: "checkbox",
            disabled: true
          }), " ", "Governance configured: environments, approval flows, Spaces, subsets, destination rules, and templates"]
        }), "\n", _jsxs(_components.li, {
          className: "task-list-item",
          children: [_jsx(_components.input, {
            type: "checkbox",
            disabled: true
          }), " ", "Storage, networking, and alerting configured per security requirements"]
        }), "\n", _jsxs(_components.li, {
          className: "task-list-item",
          children: [_jsx(_components.input, {
            type: "checkbox",
            disabled: true
          }), " ", "Marketers have the right roles and ", _jsx(_components.a, {
            href: "/workspace-management/spaces",
            children: "Space"
          }), " access to see the data they need"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Confirm with your data team or whoever owns the technical setup:"
        })
      }), "\n", _jsxs(_components.ul, {
        className: "contains-task-list",
        children: ["\n", _jsxs(_components.li, {
          className: "task-list-item",
          children: [_jsx(_components.input, {
            type: "checkbox",
            disabled: true
          }), " ", "At least one ", _jsx(_components.a, {
            href: "/sources/overview",
            children: "source connected"
          }), " with the ", _jsx(_components.a, {
            href: "/syncs/lightning-sync-engine",
            children: "Lightning sync engine"
          }), " (recommended)"]
        }), "\n", _jsxs(_components.li, {
          className: "task-list-item",
          children: [_jsx(_components.input, {
            type: "checkbox",
            disabled: true
          }), " ", "At least one ", _jsx(_components.a, {
            href: "/destinations/overview",
            children: "destination connected"
          })]
        }), "\n", _jsxs(_components.li, {
          className: "task-list-item",
          children: [_jsx(_components.input, {
            type: "checkbox",
            disabled: true
          }), " ", _jsx(_components.a, {
            href: "/customer-studio/schema",
            children: "Schema defined"
          }), " with parent model, related models, events, and traits"]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "In smaller teams, one person may own both platform admin and data team responsibilities. The checklist above is organized by responsibility, not job title."
        })
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Data teams:"
          }), " ", _jsx(_components.a, {
            href: "/getting-started/data",
            children: "Technical setup"
          }), " — connect sources, configure sync infrastructure, and define schema."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Marketers:"
          }), " ", _jsx(_components.a, {
            href: "/getting-started/business",
            children: "Daily usage"
          }), " — build audiences, run campaigns, and measure results."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
